import { useState } from "react";
import type { ReactNode } from "react";
import { IconButton, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { Authorization, dateUtil } from "@sdeapps/react-core";
import CommuneLink from "components/Links/CommuneLink";
import PerimetreLinkWithErrorBoundary from "components/Links/PerimetreLink";
import TerritoireLink from "components/Links/TerritoireLink";
import CompetenceChip from "components/Typography/CompetenceChip";
import { ApplicationRole } from "constants/ApplicationRole";
import type { Mandat } from "models";
import { elusUtil } from "utils";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";
import DeleteMandatDialog from "./DeleteMandatDialog";
import type { Operation } from "fast-json-patch";
import { compare } from "fast-json-patch";
import type { MandatSdeaFormModel } from "../../models/Mandat";
import MandatSdeaTableRowFormContent from "./MandatSdeaTableRowFormContent";
import { mandatsUtil } from "../../utils/mandatsUtil";
import { mandatsService } from "services";

interface MandatSdeaTableRowProps {
  mandatSdea: Mandat;
  refetchElu?: () => void;
}

function MandatSdeaTableRow({
  mandatSdea,
  refetchElu,
}: Readonly<MandatSdeaTableRowProps>): ReactNode {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogMandatOpen, setIsDeleteDialogMandatOpen] = useState(false);

  const formMethods = useForm<MandatSdeaFormModel>();
  const { handleSubmit } = formMethods;
  const { catchErrors, isLoading: isSending } = useSnackbarErrorHandler();

  function openEditingInterface(): void {
    setIsEditing(true);
  }

  function closeEditingInterface(): void {
    setIsEditing(false);
  }

  const editMandat: SubmitHandler<MandatSdeaFormModel> = async function (
    data: MandatSdeaFormModel
  ): Promise<void> {
    async function sendEditMandat(): Promise<void> {
      const patchOperations: Array<Operation> = compare(
        mandatSdea,
        mandatsUtil.getMandatForCompareFromFormModel(data, mandatSdea)
      );

      await mandatsService.patch(mandatSdea.id, patchOperations);

      enqueueSnackbar({
        variant: "success",
        message: ToastMessages.MODIFIED_MANDAT,
      });

      refetchElu?.();
    }

    await catchErrors(sendEditMandat);
  };

  function openDeleteMandatDialog(): void {
    setIsDeleteDialogMandatOpen(true);
  }

  function closeDeleteMandatDialog(): void {
    setIsDeleteDialogMandatOpen(false);
  }

  return (
    <>
      {isEditing ? (
        <FormProvider {...formMethods}>
          <MandatSdeaTableRowFormContent
            mandat={mandatSdea}
            sendData={handleSubmit(editMandat)}
            cancel={closeEditingInterface}
            isSending={isSending}
            variant="edit"
          />
        </FormProvider>
      ) : (
        <TableRow>
          <TableCell>
            <Typography>{elusUtil.getLibelleFonction(mandatSdea)}</Typography>
          </TableCell>
          <TableCell>
            {mandatSdea.competence != null && <CompetenceChip competence={mandatSdea.competence} />}
          </TableCell>
          <TableCell>
            {mandatSdea.idTerritoire != null && (
              <TerritoireLink
                sx={{ display: "block" }}
                id={mandatSdea.idTerritoire}
                libelle={mandatSdea.libelleTerritoire}
              />
            )}
            {mandatSdea.idPerimetre != null && (
              <PerimetreLinkWithErrorBoundary
                sx={{ display: "block" }}
                id={mandatSdea.idPerimetre}
                libelle={mandatSdea.libellePerimetre}
              />
            )}
            {mandatSdea.codeCollectivite != null && (
              <CommuneLink sx={{ display: "block" }} id={mandatSdea.codeCollectivite} />
            )}
          </TableCell>
          <TableCell width="250px">
            <Typography>
              À partir du{" "}
              {mandatSdea.dateDebutMandat != null
                ? dateUtil.format(mandatSdea.dateDebutMandat, "dd/MM/yyyy")
                : "???"}
              {mandatSdea.dateFinMandat != null &&
                ` - jusqu'au ${dateUtil.format(mandatSdea.dateFinMandat, "dd/MM/yyyy")}`}
            </Typography>
          </TableCell>

          <Authorization roles={ApplicationRole.ADMINISTRATOR}>
            <TableCell width="120px" sx={{ textAlign: "right" }} padding="none">
              {mandatSdea.dateFinMandat == null && !isEditing && (
                <IconButton onClick={openEditingInterface}>
                  <Tooltip title="Modifier">
                    <EditIcon color="info" />
                  </Tooltip>
                </IconButton>
              )}
              {!isEditing && (
                <IconButton onClick={openDeleteMandatDialog} color="error">
                  <Tooltip title="Supprimer">
                    <DeleteIcon />
                  </Tooltip>
                </IconButton>
              )}
            </TableCell>
          </Authorization>
        </TableRow>
      )}
      <DeleteMandatDialog
        open={isDeleteDialogMandatOpen}
        handleClose={closeDeleteMandatDialog}
        mandatSdea={mandatSdea}
        refetchElu={refetchElu}
      />
    </>
  );
}

export default MandatSdeaTableRow;
